import { __ } from '@wordpress/i18n';

// icons
import BassIcon from '../icons/BassIcon';
import BrassIcon from '../icons/BrassIcon';
import DrumsIcon from '../icons/DrumsIcon';
import GuitarIcon from '../icons/GuitarIcon';
import OtherInstrumentIcon from '../icons/OtherInstrumentIcon';
import PercussionIcon from '../icons/PercussionIcon';
import PianoIcon from '../icons/PianoIcon';
import StringsIcon from '../icons/StringsIcon';
import SynthIcon from '../icons/SynthIcon';
import WoodwindIcon from '../icons/WoodwindIcon';

/**
 * A rec session has one main track (the core layer) and one aux layer per
 * collaborator: the layer carries the collaborator (`assignedUser`), the
 * instrument they were invited for (`sessionType`) and the tracks they upload.
 * So "a member of the session" is simply an aux layer with an assignee.
 */

/** The instruments a collaborator can be invited for, in the picker's order. */
export const REC_INSTRUMENTS = [
    { value: 'STRINGS', label: __('Strings', 'soundbetween'), Icon: StringsIcon },
    { value: 'PERCUSSION', label: __('Percussion', 'soundbetween'), Icon: PercussionIcon },
    { value: 'DRUMS', label: __('Drums', 'soundbetween'), Icon: DrumsIcon },
    { value: 'SYNTH', label: __('Synth', 'soundbetween'), Icon: SynthIcon },
    { value: 'GUITAR', label: __('Guitar', 'soundbetween'), Icon: GuitarIcon },
    { value: 'BRASS', label: __('Brass', 'soundbetween'), Icon: BrassIcon },
    { value: 'BASS', label: __('Bass', 'soundbetween'), Icon: BassIcon },
    { value: 'WOODWIND', label: __('Woodwind', 'soundbetween'), Icon: WoodwindIcon },
];

// Session types written by earlier versions of the rec list, still readable.
const LEGACY_INSTRUMENTS = [
    { value: 'PIANO', label: __('Piano', 'soundbetween'), Icon: PianoIcon },
    { value: 'OTHER', label: __('Altro', 'soundbetween'), Icon: OtherInstrumentIcon },
];

const INSTRUMENTS_BY_VALUE = [...REC_INSTRUMENTS, ...LEGACY_INSTRUMENTS]
    .reduce((acc, item) => ({ ...acc, [item.value]: item }), {});

/** Falls back to the raw session type, so an unknown one still reads. */
export const getInstrumentLabel = (sessionType) =>
    INSTRUMENTS_BY_VALUE[sessionType]?.label ?? sessionType ?? '';

export const getInstrumentIcon = (sessionType) =>
    INSTRUMENTS_BY_VALUE[sessionType]?.Icon ?? OtherInstrumentIcon;

export const getUserFullName = (user) =>
    `${user?.name || ''} ${user?.surname || ''}`.trim();

/**
 * The collaborators of a session, one entry per user in the order they were
 * added. A user invited for two instruments owns two layers and still shows
 * up once, with both instruments.
 *
 * `team` (GET /projects/{id}/team) says whether the invitation behind the
 * layer has been accepted yet.
 */
export const getRecSessionMembers = (session, team = []) => {
    if (!session) return [];

    const statusByUser = (team || []).reduce((acc, row) => (
        row.userId ? { ...acc, [row.userId]: row.status } : acc
    ), {});

    const members = [];
    const byUser = {};

    (session.auxLayers || []).forEach(layer => {
        const user = layer.assignedUser;
        if (!user?.userId) return;

        if (!byUser[user.userId]) {
            byUser[user.userId] = {
                userId: user.userId,
                name: user.name,
                surname: user.surname,
                fullName: getUserFullName(user),
                profilePictureUrl: user.profilePictureUrl ?? user.profile_picture_url ?? null,
                // The project owner has no team row and is never pending.
                pending: statusByUser[user.userId] === 'PENDING',
                instruments: [],
                layerIds: [],
                trackCount: 0,
            };
            members.push(byUser[user.userId]);
        }

        const member = byUser[user.userId];
        if (layer.sessionType && !member.instruments.includes(layer.sessionType)) {
            member.instruments.push(layer.sessionType);
        }
        member.layerIds.push(layer.id);
        member.trackCount += layer.tracks?.length ?? 0;
    });

    return members;
};

/**
 * The aux layers the timeline shows: every one of them when nobody is picked,
 * only the picked member's otherwise.
 */
export const getVisibleAuxLayers = (session, memberUserId = null) => {
    const layers = session?.auxLayers || [];
    if (!memberUserId) return layers;
    return layers.filter(layer => layer.assignedUser?.userId === memberUserId);
};

// ---------------------------------------------------------------------------
// Read → write
//
// PUT /rec-session/{id} replaces the whole session, layers and tracks
// included, so every update resends what is already there in the snake_case
// shape the endpoint takes. Ids are sent back so layers and tracks keep them.
// ---------------------------------------------------------------------------

const TIMECODE_PATTERN = /^\d{2}:\d{2}:\d{2}(?::\d{2})?$/;

const toTimecode = (value) => (value && TIMECODE_PATTERN.test(value) ? value : null);

const toInt = (value) => {
    const n = value?.parsedValue ?? value;
    return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null;
};

const toTrackWrite = (track, index) => ({
    id: track.id,
    title: track.title || track.clip?.name || __('Traccia', 'soundbetween'),
    timecode: toTimecode(track.timecode),
    instance_duration: toInt(track.instanceDuration),
    start_playback: toInt(track.startPlayback),
    priority: toInt(track.priority),
    clip_id: track.clip?.id ?? track.clipId,
    sort_order: index,
});

const toAuxLayerWrite = (layer, index) => ({
    ...(layer.id ? { id: layer.id } : {}),
    session_type: layer.sessionType || 'OTHER',
    assigned_user_id: layer.assignedUser?.userId ?? null,
    volume: Number.isFinite(layer.volume) ? layer.volume : 100,
    sort_order: index,
    tracks: (layer.tracks || []).map(toTrackWrite),
});

/**
 * @param {object} session   a RecSessionRead
 * @param {object} [changes] `auxLayers` replaces the session's aux layers
 *                           (same read shape; a new layer has no id),
 *                           `coreTracks` the main track's
 */
export const buildRecSessionUpdateBody = (session, { auxLayers, coreTracks } = {}) => ({
    label: session.label,
    title: session.title,
    timecode: toTimecode(session.timecode),
    date: session.date,
    core_layer: {
        tracks: (coreTracks ?? session.coreLayer?.tracks ?? []).map(toTrackWrite),
    },
    aux_layers: (auxLayers ?? session.auxLayers ?? []).map(toAuxLayerWrite),
});
